import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {ResizeObserver | null} */
  #panelResizeObserver = null;
  /** @type {HTMLElement | null} */
  #panelSizedNav = null;
  /**
   * The active #syncCategoryPanelHeight's own measure-and-apply function,
   * while a category panel is open; null otherwise. #onDetailsToggle uses
   * this to force one more re-measure after a nested accordion's own
   * transition finishes — see #resyncCategoryPanelHeightAfterToggle for why
   * the ResizeObserver below can't be relied on alone for a collapse.
   * @type {(() => void) | null}
   */
  #panelApplyHeight = null;
  /**
   * True while #syncCategoryPanelHeight's own clear-measure-apply sequence is
   * writing to `nav.style.height`. Those writes change the observed elements'
   * rendered size (see the min-height comment below) and would otherwise
   * requeue this same ResizeObserver callback, looping.
   * @type {boolean}
   */
  #isSyncingCategoryPanelHeight = false;
  /**
   * True when a real ResizeObserver notification arrived *while* the guard
   * above was suppressing callbacks. A single-frame (non-animated) accordion
   * collapse only ever gets one notification — if that lands inside the
   * guarded window it must not be dropped, or the panel never re-measures
   * and is left floored at its previous (larger) height forever. Set instead
   * of re-running synchronously, and drained once the in-flight sync's guard
   * clears.
   * @type {boolean}
   */
  #categoryPanelResyncPending = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // `toggle` does not bubble, so listen in the capture phase to catch every <details>
    this.addEventListener('toggle', this.#onDetailsToggle, true);
    this.#restoreSummarySemantics();
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('toggle', this.#onDetailsToggle, true);
    this.#panelResizeObserver?.disconnect();
  }

  /**
   * Safari drops the implicit disclosure semantics of <summary> when its display is
   * not `list-item`, and the theme styles summaries as flex. Without this, iOS
   * VoiceOver announces the menu items as plain text and double-tap does nothing.
   */
  #restoreSummarySemantics() {
    for (const summary of this.querySelectorAll('summary')) {
      const details = summary.parentElement;

      if (!(details instanceof HTMLDetailsElement)) continue;

      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', String(details.open));
    }
  }

  /**
   * @param {ToggleEvent} event
   */
  #onDetailsToggle = (event) => {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement)) return;

    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(details.open));

    if (details.open) this.#closeSiblingAccordions(details);
    this.#resyncCategoryPanelHeightAfterToggle(details);
  };

  /**
   * Force a category panel height re-measure once a nested accordion's own
   * toggle transition has finished.
   *
   * The ResizeObserver in #syncCategoryPanelHeight watches the *submenu's*
   * rendered size, but the submenu is floored at `min-height: 100%` of nav's
   * last-applied height. A nested accordion collapsing to content shorter
   * than that floor doesn't change the submenu's own rendered box at all —
   * it was already sitting on the floor before the collapse, and stays
   * there after — so no resize is observable, no ResizeObserver notification
   * fires, and the panel is left floored at the stale, too-tall height
   * forever (confirmed via direct measurement: some accordions' collapsed
   * content trips a boundary that produces a detectable resize purely by
   * coincidence, others never do). Re-measuring directly off this toggle,
   * once its own transition has settled, doesn't depend on the submenu's box
   * appearing to change at all (17tqamemjmw).
   * @param {HTMLDetailsElement} details
   */
  #resyncCategoryPanelHeightAfterToggle(details) {
    if (!this.#panelApplyHeight) return;
    // Only nested accordions *inside* an open category panel need this — the
    // panel's own top-level details (which contains the submenu, rather than
    // sitting inside one) is already handled by #syncCategoryPanelHeight.
    if (!details.closest('.menu-drawer__submenu')) return;

    const candidate =
      details.querySelector(':scope > .details-content') ?? details.querySelector('.details-content');
    const content = candidate instanceof HTMLElement ? candidate : details;

    // Deliberately a plain `transitionend` listener (matching
    // #closeAccordionSmoothly's own pattern below), not
    // getAnimations()/onAnimationEnd: `{ subtree: true }` there picks up
    // *any* animation running anywhere inside this accordion's content, not
    // just its own collapse — an unrelated one (e.g. a badge's looping
    // animation) can leave Promise.allSettled() waiting on an animation that
    // never finishes, silently dropping the re-measure. Filtering
    // `transitionend` down to this exact element and property is immune to
    // that.
    let settled = false;

    const resync = () => {
      if (settled) return;
      settled = true;
      content.removeEventListener('transitionend', onTransitionEnd);
      this.#panelApplyHeight?.();
    };

    /** @param {TransitionEvent} event */
    const onTransitionEnd = (event) => {
      if (event.target !== content) return;
      if (event.propertyName !== 'block-size') return;
      resync();
    };

    content.addEventListener('transitionend', onTransitionEnd);
    // Fallback if transitionend doesn't fire (reduced motion, no block-size change, etc.)
    window.setTimeout(resync, 500);
  }

  /**
   * When an accordion menu opens, close other open menus at the same level.
   * @param {HTMLDetailsElement} details
   */
  #closeSiblingAccordions(details) {
    if (!(details.parentElement instanceof HTMLElement)) return;
    if (details.parentElement.tagName !== 'ACCORDION-CUSTOM') return;

    const listItem = details.parentElement.closest('li');
    const list = listItem?.parentElement;
    if (!list) return;

    for (const sibling of list.children) {
      if (sibling === listItem) continue;
      const siblingDetails = sibling.querySelector(':scope > accordion-custom > details[open]');
      if (siblingDetails instanceof HTMLDetailsElement) {
        this.#closeAccordionSmoothly(siblingDetails);
      }
    }
  }

  /**
   * Close an accordion with its height transition.
   * Programmatic closes skip the theme's focus-within transition gate, so we
   * temporarily force animation on.
   * @param {HTMLDetailsElement} details
   */
  #closeAccordionSmoothly(details) {
    details.dataset.animatingClose = '';
    // Apply transition styles before removing [open], otherwise the close snaps.
    void details.offsetHeight;

    const candidate =
      details.querySelector(':scope > .details-content') ?? details.querySelector('.details-content');
    const content = candidate instanceof HTMLElement ? candidate : null;

    const finish = () => {
      delete details.dataset.animatingClose;
      content?.removeEventListener('transitionend', onTransitionEnd);
    };

    /**
     * @param {TransitionEvent} event
     */
    const onTransitionEnd = (event) => {
      if (event.target !== content) return;
      if (event.propertyName !== 'block-size' && event.propertyName !== 'opacity') return;
      finish();
    };

    content?.addEventListener('transitionend', onTransitionEnd);
    details.open = false;

    // Fallback if transitionend doesn't fire (reduced motion, missing content, etc.)
    setTimeout(finish, 500);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Prevent the native <details> toggle — it races with our animated close and
    // can leave `open` set without `menu-open`, requiring a second click to open.
    if (event instanceof Event) {
      event.preventDefault();
    }

    return this.isOpen ? this.close() : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Clicking a <summary> also runs the browser's own toggle as its default action,
    // which reads the (by-then-already-open) `open` attribute set below and flips it
    // straight back to closed — the explicit state this method sets never sticks, and
    // a closed <details> keeps its content out of the render tree, giving any panel
    // inside it (this drawer's own .menu-drawer__submenu / .menu-drawer) a 0px box
    // regardless of its own CSS. toggle() already prevents this for the drawer's own
    // open/close; do the same here so every on:click="header-drawer/open/..." caller
    // (the localization picker, and the category-menu submenus generalising it) is safe.
    if (event instanceof Event) {
      event.preventDefault();
    }

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    this.#ensureHeaderVisible();

    // Measure once after layout — repeated offset updates while sticky settles cause shake
    this.#updateDrawerOffset();

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
        this.#syncCategoryPanelHeight(details);
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Mirror a category drill-down panel's real content height onto its nav
   * region, so nav — and the promo card and footer links after it — grow to
   * fit the panel instead of the panel scrolling on its own inside a
   * fixed-size nav (17tqamemjmw). No-ops for the region/language picker's
   * submenu, which is a sibling of nav rather than a descendant and still
   * covers the whole drawer (per #296).
   *
   * The submenu is styled `min-height: 100%` of nav (so a short panel still
   * covers the top-level list underneath it, see the CSS comment). Once nav
   * has an explicit height, that floor holds the submenu's box at the last
   * applied height even after its content shrinks — so a naive re-measure on
   * every resize only ever grows: closing a nested accordion never brings the
   * height back down (17tqamemjmw). Clearing nav's explicit height before
   * measuring lets the submenu relax to its true content height first, so
   * shrinks are seen as well as growth.
   * @param {HTMLDetailsElement} details
   */
  #syncCategoryPanelHeight(details) {
    const nav = details.closest('.menu-drawer__navigation');
    const submenu = details.querySelector(':scope > .menu-drawer__submenu');
    const topList = nav?.querySelector(':scope > .menu-drawer__menu.has-submenu');

    if (!(nav instanceof HTMLElement) || !(submenu instanceof HTMLElement) || !(topList instanceof HTMLElement)) {
      return;
    }

    this.#clearCategoryPanelHeight();

    const applyHeight = () => {
      // Re-entrancy guard: clearing/re-applying nav's height below changes
      // the rendered size of `submenu` (its `min-height: 100%` is relative to
      // nav), which is one of the elements this same ResizeObserver watches.
      // Without this guard that requeues this callback, which writes the
      // height again, which requeues it again — a loop. While a sync is in
      // flight, don't run a second one re-entrantly — but don't drop it
      // either: a non-animated accordion collapse reports its size change in
      // a single notification, and if that one notification landed inside
      // this window it's the only chance to see the shrink, so remember it
      // and replay it once the in-flight sync's guard clears below.
      if (this.#isSyncingCategoryPanelHeight) {
        this.#categoryPanelResyncPending = true;
        return;
      }
      this.#isSyncingCategoryPanelHeight = true;

      // Clear the explicit height first so the submenu's `min-height: 100%`
      // floor relaxes to nav's natural (top-level-list) height, and the
      // browser can lay out the submenu's *current* content before we read
      // it — otherwise scrollHeight below would just report the stale,
      // already-applied height back to us.
      nav.style.height = '';
      void nav.offsetHeight; // Force layout so the measurement below sees the cleared height.

      nav.style.height = `${Math.max(topList.scrollHeight, submenu.scrollHeight)}px`;

      requestAnimationFrame(() => {
        this.#isSyncingCategoryPanelHeight = false;

        if (this.#categoryPanelResyncPending) {
          this.#categoryPanelResyncPending = false;
          applyHeight();
        }
      });
    };

    applyHeight();
    this.#panelSizedNav = nav;
    this.#panelApplyHeight = applyHeight;
    this.#panelResizeObserver = new ResizeObserver(applyHeight);
    this.#panelResizeObserver.observe(submenu);
    this.#panelResizeObserver.observe(topList);
  }

  /**
   * Stop measuring the current category panel and let nav return to its
   * natural (top-level list) height.
   */
  #clearCategoryPanelHeight() {
    this.#panelResizeObserver?.disconnect();
    this.#panelResizeObserver = null;
    this.#panelApplyHeight = null;
    this.#isSyncingCategoryPanelHeight = false;
    this.#categoryPanelResyncPending = false;

    if (this.#panelSizedNav) {
      this.#panelSizedNav.style.height = '';
      this.#panelSizedNav = null;
    }
  }

  /**
   * Keep sticky header in view when opening the drawer mid-scroll.
   * Do not force sticky at the top of the page — that pulls the header over the
   * announcement and makes the drawer start too high.
   */
  #ensureHeaderVisible() {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;

    const stickyMode = header.getAttribute('sticky');
    if (stickyMode !== 'always' && stickyMode !== 'scroll-up') return;

    // Already stuck — leave state alone to avoid intersection-observer thrash
    if (header.dataset.stickyState === 'active') return;

    const rect = header.getBoundingClientRect();
    if (rect.top < 0) {
      header.dataset.stickyState = 'active';
    }
  }

  /**
   * Position the drawer below the site header (and announcement when visible)
   * so the original header stays in place with the menu → close toggle.
   */
  #updateDrawerOffset() {
    const header = document.querySelector('#header-component');
    let offset = 0;

    if (header instanceof HTMLElement) {
      const headerRect = header.getBoundingClientRect();

      if (headerRect.bottom > 0) {
        offset = Math.ceil(headerRect.bottom);
      } else {
        offset = this.#readCssPx(document.body, '--header-height') || Math.ceil(header.offsetHeight);
      }
    } else {
      const announcement = document.querySelector('.announcement-bar');
      if (announcement instanceof HTMLElement) {
        offset = Math.max(0, Math.ceil(announcement.getBoundingClientRect().bottom));
      }
    }

    // Written to <body>, not `this` (the <header-drawer> element): the header section
    // hydrates itself shortly after page load (sections/header.liquid's `hydrate()`),
    // and that morph blindly overwrites the drawer's `style` attribute with the
    // server-rendered one, silently dropping any property set here. --header-height
    // and --header-group-height already live on <body> for the same reason.
    document.body.style.setProperty('--menu-drawer-offset', `${Math.max(0, offset)}px`);
  }

  /**
   * @param {Element} element
   * @param {string} property
   * @returns {number}
   */
  #readCssPx(element, property) {
    const raw = getComputedStyle(element).getPropertyValue(property).trim();
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? Math.round(value) : 0;
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the panel's transform transition before removing [open].
    // Removing [open] early hides <details> content and makes the close feel like a snap.
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    const panel = drawer instanceof HTMLElement ? drawer : details;
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      this.#clearCategoryPanelHeight();
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    /**
     * @param {TransitionEvent} event
     */
    const onTransitionEnd = (event) => {
      if (event.target !== panel) return;
      if (event.propertyName !== 'transform') return;
      finish();
    };

    panel.addEventListener('transitionend', onTransitionEnd);

    // Fallback if transitionend doesn't fire (reduced motion, no transform change, etc.)
    const durationMs = this.#getDrawerAnimationMs(panel);
    setTimeout(finish, durationMs + 50);

    // Also resolve via Web Animations API when the browser reports the transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        onAnimationEnd(panel, finish, { subtree: false });
      });
    });
  }

  /**
   * @param {HTMLElement} panel
   * @returns {number}
   */
  #getDrawerAnimationMs(panel) {
    const raw = getComputedStyle(panel).getPropertyValue('--drawer-animation-speed').trim()
      || getComputedStyle(document.documentElement).getPropertyValue('--drawer-animation-speed').trim()
      || '0.2s';

    if (raw.endsWith('ms')) {
      return Number.parseFloat(raw) || 200;
    }

    return Math.round((Number.parseFloat(raw) || 0.2) * 1000);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
